import type { AppView } from "../router/Router";

/** Canonical origin. Keep in sync with index.html / robots.txt / sitemap.xml. */
export const SITE_URL = "https://hupper.fun";

export interface RouteMeta {
  title: string;
  /** Left undefined for app/transactional views (the existing tag is kept). */
  description?: string;
  /** Whether search engines should index the route. */
  index: boolean;
  /** Canonical path (leading slash). */
  path: string;
}

const DEFAULT_DESC =
  "Bet USDT on skill-based 1v1 games. Every match is settled on-chain on Solana — non-custodial, low fees, instant payouts.";

/** Public, indexable content/marketing routes — unique title + description. */
const INDEX: Partial<Record<AppView, { title: string; description: string; path: string }>> = {
  dashboard: {
    title: "Hupper — Skill-based 1v1 wagering on Solana",
    description: DEFAULT_DESC,
    path: "/",
  },
  leaderboard: {
    title: "Live wagering leaderboard — Hupper",
    description:
      "See who's winning USDT in skill-based 1v1 matches on Hupper. Live rankings across FPS, Pong, Snake and more, settled on-chain on Solana.",
    path: "/leaderboard",
  },
  tournaments: {
    title: "Crypto 1v1 tournaments — Hupper",
    description:
      "Compete in skill-based 1v1 tournaments on Hupper for USDT prize pools, settled on-chain on Solana.",
    path: "/tournaments",
  },
  docs: {
    title: "Documentation — Hupper",
    description:
      "How Hupper works: non-custodial escrow, provably fair matches, fees and instant payouts for skill-based 1v1 wagering on Solana.",
    path: "/docs",
  },
  legal: {
    title: "Legal — Hupper",
    description:
      "Hupper legal information: terms of service, privacy, responsible gaming and restricted regions.",
    path: "/legal",
  },
  terms: {
    title: "Terms of Service — Hupper",
    description: "The terms governing use of Hupper's skill-based 1v1 wagering protocol on Solana.",
    path: "/terms",
  },
  privacy: {
    title: "Privacy Policy — Hupper",
    description: "How Hupper handles your data — the privacy policy for the skill-based wagering protocol.",
    path: "/privacy",
  },
  "responsible-gaming": {
    title: "Responsible Gaming — Hupper",
    description: "Hupper's responsible-gaming guidance for skill-based wagering.",
    path: "/responsible-gaming",
  },
  "restricted-regions": {
    title: "Restricted Regions — Hupper",
    description: "Regions where Hupper's skill-based wagering is not available.",
    path: "/restricted-regions",
  },
};

/** App / transactional / auth routes — title only, kept out of the index. */
const NOINDEX: Partial<Record<AppView, { title: string; path: string }>> = {
  pong: { title: "Pong 1v1 — Hupper", path: "/pong" },
  fps: { title: "FPS Arena 1v1 — Hupper", path: "/fps" },
  "fps-stake": { title: "FPS Arena 1v1 — Hupper", path: "/fps" },
  "fps-briefing": { title: "FPS Arena 1v1 — Hupper", path: "/fps" },
  snake: { title: "Snake 1v1 — Hupper", path: "/snake" },
  "snake-stake": { title: "Snake 1v1 — Hupper", path: "/snake" },
  math: { title: "Math Duel 1v1 — Hupper", path: "/math" },
  "math-stake": { title: "Math Duel 1v1 — Hupper", path: "/math" },
  "tower-defense": { title: "Tower Defense 1v1 — Hupper", path: "/" },
  "td-stake": { title: "Tower Defense 1v1 — Hupper", path: "/" },
  "tournament-math": { title: "Tournament match — Hupper", path: "/tournaments" },
  "tournament-td": { title: "Tournament match — Hupper", path: "/tournaments" },
  "custom-fps": { title: "Friend match — Hupper", path: "/" },
  "custom-pong": { title: "Friend match — Hupper", path: "/" },
  "custom-math": { title: "Friend match — Hupper", path: "/" },
  "custom-td": { title: "Friend match — Hupper", path: "/" },
  profile: { title: "Profile — Hupper", path: "/profile" },
  stats: { title: "Stats — Hupper", path: "/stats" },
  status: { title: "System status — Hupper", path: "/status" },
  settings: { title: "Settings — Hupper", path: "/settings" },
  admin: { title: "Admin — Hupper", path: "/admin" },
};

/** Resolve the document metadata for a given app view. */
export function metaForView(view: AppView): RouteMeta {
  const indexed = INDEX[view];
  if (indexed) return { ...indexed, index: true };
  const noindexed = NOINDEX[view];
  if (noindexed) return { ...noindexed, index: false };
  // Unknown / future view → stay out of the index, fall back to the brand title.
  return { title: "Hupper — Skill-based 1v1 wagering on Solana", index: false, path: "/" };
}

/** Indexable paths, for sitemap.xml — keep the sitemap in sync with this. */
export const INDEXABLE_PATHS: string[] = Object.values(INDEX).flatMap((m) => (m ? [m.path] : []));
